<script setup>
import { defineProps, onMounted } from "vue";
import GenericAccordion from "@/components/ui/accordion/GenericAccordion.vue";
import Input from "@/components/ui/input/Input.vue";
import { computed } from "vue";
import GenericCard from "@/components/ui/card/GenericCard.vue";
import PhoneInputWithDialCode from "./PhoneInputWithDialCode.vue";
import { useStates, useLocalties } from "@/composables";
import { storeToRefs } from "pinia";
import { usePlaceholders } from "@/composables";
import Combobox from "@/components/ui/combobox/Combobox.vue";
import Button from "@/components/ui/button/Button.vue";
import { Trash2 } from "lucide-vue-next";

const states = useStates()
const localties = useLocalties()

const {apiStates} = storeToRefs(states)
const {fetchStates} = states
const {apiLocalties, fetchingLocalties} = storeToRefs(localties)
const {fetchLocalties} = localties

const {statePlaceholder, localtyPlaceholder} = usePlaceholders()

const props = defineProps({
    title: { type: String, required: true },
    hub: { type: Object, required: true },
    countryId: { type: [String, Number], required: true },
    validationErrors: { type: Object, default: () => {} },
    hubIndex: { type: [String, Number], required: true },
});

const emit = defineEmits(["update:hub", "remove:hub"]);

const model = computed({
    get() {
        return props.hub;
    },
    set(value) {
        emit("update:hub", value);
    },
});

const handleStateChange = async (stateId) => {
    if(!stateId) {
        model.value.lga_id = undefined
        return;
    }
   await fetchLocalties(stateId)
}

onMounted(async () => {
    await fetchStates(props.countryId)

    if(model.value.state_id) {
        await fetchLocalties(model.value.state_id)
    }
})

const removeHub = () => {
    emit("remove:hub");
}

const computedValidationErrors = (key) => {
    return props.validationErrors[key] || [];
}
</script>

<template>
    <GenericCard class="bg-neutral-25">
        <template #content>
            <GenericAccordion :value="title" item-class="border-0" trigger-class="pb-0" content-class="pb-0">
                <template #title>
                    <div class="flex flex-col justify-start items-start">
                        <div class="text-sm font-semibold text-primary-font">
                            {{ title }}
                        </div>
                    </div>
                </template>

                <div class="">
                    <div class="mt-4 mb-2 px-1">
                        <Input
                            v-model="model.name"
                            type="text"
                            placeholder="Enter market name"
                            class="shadow-none placeholder:text-text-soft bg-white"
                        />
                        <p v-for="error in (computedValidationErrors(`payload.hubs.${hubIndex}.name`) || [])" :key="error" class="text-red-500 text-sm">
                            {{ error }}
                        </p>
                    </div>
                    <div class="mt-4 mb-2 px-1">
                        <Input
                            v-model="model.leader_name"
                            type="text"
                            placeholder="Enter market manager / leader name (optional)"
                            class="shadow-none placeholder:text-text-soft bg-white"
                        />
                        <p v-for="error in (computedValidationErrors(`payload.hubs.${hubIndex}.leader_name`) || [])" :key="error" class="text-red-500 text-sm">
                            {{ error }}
                        </p>
                    </div>
                    <div class="mt-4 mb-2 px-1">
                        <PhoneInputWithDialCode 
                            v-model="model.leader_phone" 
                            :country-name="model.country_name"
                            :country-code="model.country_code"
                            :country-flag="model.country_flag"
                        />
                        <p v-for="error in (computedValidationErrors(`payload.hubs.${hubIndex}.leader_phone`) || [])" :key="error" class="text-red-500 text-sm">
                            {{ error }}
                        </p>
                    </div>
                    <div class="mt-4 mb-2 px-1">
                        <Input
                            v-model="model.address"
                            type="text"
                            placeholder="Enter address"
                            class="shadow-none placeholder:text-text-soft bg-white"
                        />
                        <p v-for="error in (computedValidationErrors(`payload.hubs.${hubIndex}.address`) || [])" :key="error" class="text-red-500 text-sm">
                            {{ error }}
                        </p>
                    </div>
                    <div class="mt-4 mb-2 px-1">
                        <Combobox v-model="model.state_id" :options="apiStates" :label-key="'name'" :value-key="'id'" :placeholder="statePlaceholder" @change="handleStateChange"></Combobox>
                        <p v-for="error in (computedValidationErrors(`payload.hubs.${hubIndex}.state_id`) || [])" :key="error" class="text-red-500 text-sm">
                            {{ error }}
                        </p>
                    </div>
                    <div class="mt-4 mb-2 px-1">
                        <Combobox v-model="model.lga_id" :options="apiLocalties" :label-key="'name'" :value-key="'id'" :placeholder="localtyPlaceholder" :loading="fetchingLocalties"></Combobox>
                        <p v-for="error in (computedValidationErrors(`payload.hubs.${hubIndex}.lga_id`) || [])" :key="error" class="text-red-500 text-sm">
                            {{ error }}
                        </p>
                    </div>
                    <div class="flex justify-end mt-4 px-1 text-primary-red">
                        <Button variant="ghost" @click="removeHub" size="sm">
                            <span>Delete</span>
                            <Trash2 />
                        </Button>
                    </div>
                </div>
            </GenericAccordion>
        </template>
    </GenericCard>
</template>